/**
 * ImportScope - per-table control for HOW MUCH history to pull on the first
 * backfill. Visual + interactive:
 *
 *   Auto         → let the platform fit it to your plan (default)
 *   Most recent  → a slider + presets + exact number (newest N rows)
 *   Date range   → from/to (only when the table has a time column)
 *
 * Emits a `scope` object: { mode: 'auto'|'recent'|'range', limit, from, to }.
 */

import React from 'react';
import {
  Box, Stack, Typography, ToggleButton, ToggleButtonGroup, Slider, TextField,
  Chip, useTheme,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { compactNum } from './connectorMeta';

const PRESETS = [10000, 100000, 500000, 1000000];

export default function ImportScope({ scope, onChange, rowEstimate, hasTimestamp, signalCols = 1 }) {
  const theme = useTheme();
  const mode = scope?.mode || 'auto';
  const limit = scope?.limit ?? (rowEstimate ? Math.min(rowEstimate, 100000) : 100000);
  const max = rowEstimate && rowEstimate > 0 ? rowEstimate : 1000000;
  const set = (patch) => onChange?.({ ...scope, ...patch });

  const estSignals = limit * Math.max(1, signalCols);

  return (
    <Box sx={{ mt: 1, pt: 1.25, borderTop: `1px dashed ${theme.palette.divider}` }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: 'text.secondary' }}>
          Import scope
        </Typography>
        <ToggleButtonGroup size="small" exclusive value={mode} onChange={(_, v) => v && set({ mode: v })}>
          <ToggleButton value="auto" sx={{ textTransform: 'none', px: 1.5, fontSize: 12.5 }}>
            <AutoAwesomeIcon sx={{ fontSize: 14, mr: 0.5 }} />Auto
          </ToggleButton>
          <ToggleButton value="recent" sx={{ textTransform: 'none', px: 1.5, fontSize: 12.5 }}>Most recent</ToggleButton>
          <ToggleButton value="range" disabled={!hasTimestamp} sx={{ textTransform: 'none', px: 1.5, fontSize: 12.5 }}>Date range</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {mode === 'auto' && (
        <Typography variant="caption" color="text.secondary">
          We’ll import as much history as your plan allows (newest first), then keep syncing new data.
        </Typography>
      )}

      {mode === 'recent' && (
        <Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Slider
              size="small" value={Math.min(limit, max)} min={1} max={max}
              step={Math.max(1, Math.round(max / 100))}
              onChange={(_, v) => set({ limit: v })}
              valueLabelDisplay="auto" valueLabelFormat={(v) => compactNum(v)}
              sx={{ flex: 1 }}
            />
            <TextField size="small" type="number" value={limit}
              onChange={(e) => set({ limit: Math.max(1, parseInt(e.target.value || '0', 10)) })}
              sx={{ width: 120, '& input': { fontSize: 13 } }} />
          </Stack>
          <Stack direction="row" spacing={0.75} sx={{ mt: 0.75, flexWrap: 'wrap', gap: 0.75 }}>
            {PRESETS.filter((p) => p <= max * 1.0001).map((p) => (
              <Chip key={p} size="small" clickable variant={limit === p ? 'filled' : 'outlined'}
                color={limit === p ? 'primary' : 'default'} label={compactNum(p)} onClick={() => set({ limit: p })}
                sx={{ height: 22, fontSize: 11 }} />
            ))}
            {rowEstimate != null && (
              <Chip size="small" clickable variant={limit >= max ? 'filled' : 'outlined'}
                color={limit >= max ? 'primary' : 'default'} label="All" onClick={() => set({ limit: max })}
                sx={{ height: 22, fontSize: 11 }} />
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            ~{compactNum(limit)} newest rows ≈ {compactNum(estSignals)} signals
          </Typography>
        </Box>
      )}

      {mode === 'range' && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
          <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }}
            value={scope?.from || ''} onChange={(e) => set({ from: e.target.value })} sx={{ '& input': { fontSize: 13 } }} />
          <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }}
            value={scope?.to || ''} onChange={(e) => set({ to: e.target.value })} sx={{ '& input': { fontSize: 13 } }} />
        </Stack>
      )}
    </Box>
  );
}
